import { Settings } from '@atlaskit/atlassian-navigation';
import { IconButton } from '@atlaskit/button/new';
import PersonIcon from '@atlaskit/icon/core/person';
import SettingsIcon from '@atlaskit/icon/core/settings';
import OldPersonIcon from '@atlaskit/icon/glyph/person';
import { MenuGroup, Section } from '@atlaskit/menu';
import Popup from '@atlaskit/popup';
import Tooltip from '@atlaskit/tooltip';
import { MenuListItem } from '@atlassian/navigation-system/v4';
import React, { Suspense, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { PreloadedQuery, useQueryLoader } from 'react-relay';

import { useAnalytics } from '@townsquare/analytics';
import { settingsRoute } from '@townsquare/settings-view/route';
import { useIsNavRefreshEnabled } from '@townsquare/stat-sig/nav4';
import {
  useNoWorkspaceExperience,
  useProductDisplayNameFull,
  useWorkspaceGlobalId,
} from '@townsquare/workspace-store/hooks';

import { RouterLinkItem } from '../RouterLinkItem';

import { AdminLinks, SettingsAdminLinks } from './AdminLinks';
import { FieldSettingsLink } from './FieldSettingsLink';
import { WorkspaceSettingsLink } from './WorkspaceSettingsLink';
import AdminLinksQuery, { AdminLinksQuery as AdminLinksQueryType } from './__generated__/AdminLinksQuery.graphql';

const minWidth = 240;
const maxWidth = 400;

export const SettingsContent = ({
  onClick,
  queryRef,
}: {
  onClick: () => void;
  queryRef: PreloadedQuery<AdminLinksQueryType> | null | undefined;
}) => {
  const analytics = useAnalytics();
  const noWorkspaceExperience = useNoWorkspaceExperience();
  const productDisplayName = useProductDisplayNameFull();
  const intl = useIntl();
  const isNavRefreshEnabled = useIsNavRefreshEnabled();

  const handlePersonalSettingsClick = () => {
    void analytics.ui('personalSettings', 'clicked');
  };

  return (
    <MenuGroup onClick={onClick} minWidth={minWidth} maxWidth={maxWidth}>
      {noWorkspaceExperience ? (
        <AdminLinks />
      ) : (
        <Section
          title={intl.formatMessage(
            {
              id: 'townsquare.navigation.settings.product-settings-heading',
              description: 'Heading for a settings dropdown including links to settings pages of the product',
              defaultMessage: '{productDisplayName} settings',
            },
            {
              productDisplayName: productDisplayName,
            },
          )}
        >
          <>
            {queryRef && (
              <ErrorBoundary fallbackRender={() => null}>
                <Suspense fallback={false}>
                  <WorkspaceSettingsLink queryRef={queryRef} />
                </Suspense>
              </ErrorBoundary>
            )}
            <RouterLinkItem
              onClick={handlePersonalSettingsClick}
              to={settingsRoute}
              attributes={{ params: { pageName: 'personal' } }}
              testId="nav-personal-settings-link"
              iconBefore={isNavRefreshEnabled && <PersonIcon label="" LEGACY_fallbackIcon={OldPersonIcon} />}
              description={
                isNavRefreshEnabled ? (
                  <FormattedMessage
                    id="townsquare.navigation.settings.personal-description"
                    defaultMessage="Manage notification preferences and themes"
                    description="Description for the personal settings link in the settings dropdown"
                  />
                ) : undefined
              }
            >
              <FormattedMessage
                id="townsquare.navigation.settings.personal-settings"
                defaultMessage="Personal settings"
                description="Label for the personal settings link in the settings dropdown"
              />
            </RouterLinkItem>
            {isNavRefreshEnabled && queryRef && (
              <ErrorBoundary fallbackRender={() => null}>
                <Suspense fallback={false}>
                  <FieldSettingsLink queryRef={queryRef} />
                </Suspense>
              </ErrorBoundary>
            )}
          </>
        </Section>
      )}
      {queryRef && (
        <ErrorBoundary fallbackRender={() => null}>
          <Suspense fallback={false}>
            <SettingsAdminLinks queryRef={queryRef} />
          </Suspense>
        </ErrorBoundary>
      )}
    </MenuGroup>
  );
};

export const SettingsPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const analytics = useAnalytics();
  const intl = useIntl();
  const [globalId] = useWorkspaceGlobalId();
  const noWorkspaceExperience = useNoWorkspaceExperience();
  const isNewNavExperience = useIsNavRefreshEnabled();

  const onClick = () => {
    void analytics.ui('settingsCog', 'clicked');
    setIsOpen(!isOpen);
  };

  const onClose = () => {
    setIsOpen(false);
  };

  const onMouseEnter = () => {
    if (!noWorkspaceExperience) {
      loadQuery({
        id: globalId,
      });
    }
  };

  const [queryRef, loadQuery] = useQueryLoader<AdminLinksQueryType>(AdminLinksQuery);

  return (
    <Popup
      placement="bottom-end"
      content={() => <SettingsContent onClick={onClose} queryRef={queryRef} />}
      isOpen={isOpen}
      onClose={onClose}
      trigger={triggerProps => {
        if (isNewNavExperience) {
          return (
            <MenuListItem>
              <Tooltip
                content={intl.formatMessage({
                  id: 'townsquare.navigation.settings.tooltip',
                  defaultMessage: 'Settings',
                  description: 'Tooltip for the settings cog in the navigation',
                })}
              >
                <IconButton
                  onClick={onClick}
                  onMouseEnter={onMouseEnter}
                  isSelected={isOpen}
                  appearance="subtle"
                  icon={SettingsIcon}
                  {...triggerProps}
                  label={intl.formatMessage({
                    id: 'townsquare.navigation.settings.button',
                    defaultMessage: 'Settings button',
                    description: 'Setting button in the top nav',
                  })}
                  shape="circle"
                  testId="nav-settings-button-refreshed"
                />
              </Tooltip>
            </MenuListItem>
          );
        }
        return (
          <Settings
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            isSelected={isOpen}
            tooltip={intl.formatMessage({
              id: 'townsquare.navigation.settings.tooltip',
              defaultMessage: 'Settings',
              description: 'Tooltip for the settings cog in the navigation',
            })}
            testId="nav-settings-button"
            {...triggerProps}
          />
        );
      }}
    />
  );
};
